/** Derive viewMode at a specific tool invocation from chat history (not live session state). */

type ToolPart = {
  type?: string;
  toolInvocation?: {
    toolName?: string;
    state?: string;
    result?: { view_mode?: string; status?: string };
    args?: { mode?: string };
  };
};

type MessageLike = {
  id: string;
  role?: string;
  parts?: ToolPart[];
};

/**
 * Walk assistant messages in order and return the view_mode that was active
 * when the tool at (targetMessageId, targetPartIndex) started — i.e. after
 * every prior switch_mode / upload_alert in the transcript, but before this tool.
 */
export function effectiveViewModeAtTool(args: {
  messages: MessageLike[];
  targetMessageId: string;
  targetPartIndex: number;
  /** Live streaming parts for the target message (override persisted parts). */
  liveParts?: ToolPart[];
  /** When set (/alerts/[id] only), mode never leaves alert/signal. */
  lockedViewMode?: string | null;
}): string {
  const {
    messages,
    targetMessageId,
    targetPartIndex,
    liveParts,
    lockedViewMode = null,
  } = args;

  // Unlocked /chat sessions always start slate — ignore stale session.view_mode in Mongo.
  // Mode changes only via switch_mode / upload_alert events in the transcript.
  let mode = lockedViewMode ?? "research";

  for (const msg of messages) {
    if (msg.role !== "assistant") continue;

    const parts =
      msg.id === targetMessageId && liveParts && liveParts.length > 0
        ? liveParts
        : Array.isArray(msg.parts)
          ? msg.parts
          : [];

    for (let i = 0; i < parts.length; i++) {
      if (msg.id === targetMessageId && i === targetPartIndex) {
        return mode;
      }

      const part = parts[i];
      if (part?.type !== "tool-invocation") continue;

      const inv = part.toolInvocation;
      if (!inv) continue;

      if (inv.toolName === "switch_mode" && inv.state === "result") {
        const next = inv.result?.view_mode;
        if (typeof next === "string" && next) {
          if (lockedViewMode && next !== lockedViewMode) {
            // Alert/signal pages stay locked — mirror chat.tsx guard.
          } else {
            mode = next;
          }
        }
      }

      if (
        inv.toolName === "upload_alert" &&
        inv.state === "result" &&
        inv.result?.status === "success" &&
        !lockedViewMode
      ) {
        mode = "research";
      }
    }
  }

  return mode;
}
